import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, Phone, Mail, MessageSquare, Trash2, X, Loader2, 
  Sparkles, LayoutGrid, List, MessageCircle, 
  Send, UserCheck, Plus, Download, CheckCircle2, AlertCircle
} from 'lucide-react';
import { leadsService, type Lead, type LeadStatus, type LeadSource } from '../../services/leadsService';

export function CrmLeads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [activeView, setActiveView] = useState<'kanban' | 'table' | 'simulator'>('kanban');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  // Notifications & Loaders
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Forms
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    phone: '',
    source: 'website' as LeadSource,
    courseInterest: 'Forward Deployed Engineer (FDE)',
    notes: '',
  });

  const [followUpForm, setFollowUpForm] = useState<{
    note: string;
    type: 'whatsapp_inbound' | 'whatsapp_outbound' | 'call' | 'note' | 'meeting';
    nextFollowUp: string;
    newStatus: LeadStatus | '';
  }>({
    note: '',
    type: 'call',
    nextFollowUp: '',
    newStatus: '',
  });

  const [convertForm, setConvertForm] = useState({
    notes: 'Converted via WhatsApp & counseling session',
  });

  // Simulator Form
  const [simulatorForm, setSimulatorForm] = useState({
    name: 'Rahul Sharma',
    phone: '+91 9876543210',
    source: 'meta-ads',
    message: 'Hi Cloud Binary Admissions Team, I saw your Meta Ad for the Forward Deployed Engineer (FDE) program and would like to speak with a mentor. [REF:META_ADS]',
    courseInterest: 'Forward Deployed Engineer (FDE)',
  });

  useEffect(() => {
    loadLeads();
    if (searchParams.get('action') === 'create') {
      setShowCreateModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    filterLeads();
  }, [leads, searchQuery, statusFilter, sourceFilter]);

  const loadLeads = async () => {
    try {
      const data = await leadsService.listLeads();
      setLeads(data);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to load leads' });
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (lead) =>
          lead.name?.toLowerCase().includes(q) ||
          lead.email?.toLowerCase().includes(q) ||
          lead.phone?.includes(q) ||
          lead.courseInterest?.toLowerCase().includes(q) ||
          lead.source?.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    }

    if (sourceFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.source === sourceFilter);
    }

    setFilteredLeads(filtered);
  };

  const handleCreateLead = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const newLead = await leadsService.createLead({
        name: createForm.name,
        email: createForm.email,
        phone: createForm.phone,
        source: createForm.source,
        courseInterest: createForm.courseInterest,
        notes: createForm.notes,
      });

      setLeads([newLead, ...leads]);
      setMessage({ type: 'success', text: `Lead for ${newLead.name} created successfully!` });
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        email: '',
        phone: '',
        source: 'website',
        courseInterest: 'Forward Deployed Engineer (FDE)',
        notes: '',
      });
      setSearchParams({});
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to create lead' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (leadId: string, newStatus: LeadStatus) => {
    try {
      const updated = await leadsService.updateLead(leadId, { status: newStatus });
      setLeads(leads.map((l) => (l.leadId === leadId ? updated : l)));
      setMessage({ type: 'success', text: `Lead status updated to ${newStatus}` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update status' });
    }
  };

  const handleAddFollowUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLead) return;

    setActionLoading(true);
    try {
      const updated = await leadsService.addFollowUp(
        selectedLead.leadId,
        followUpForm.note,
        followUpForm.nextFollowUp || undefined,
        followUpForm.type,
        'Admissions Counselor',
        followUpForm.newStatus || undefined
      );

      setLeads(leads.map((l) => (l.leadId === selectedLead.leadId ? updated : l)));
      setMessage({ type: 'success', text: 'Counselor follow-up added successfully' });
      setShowFollowUpModal(false);
      setSelectedLead(null);
      setFollowUpForm({ note: '', type: 'call', nextFollowUp: '', newStatus: '' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to add follow-up' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLead) return;

    setActionLoading(true);
    try {
      const result = await leadsService.convertToStudent(selectedLead.leadId, convertForm.notes);
      setLeads(leads.map((l) => (l.leadId === selectedLead.leadId ? result.lead : l)));
      setMessage({ type: 'success', text: `Lead ${selectedLead.name} successfully converted to Enrolled Student!` });
      setShowConvertModal(false);
      setSelectedLead(null);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to convert lead' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await leadsService.deleteLead(leadId);
      setLeads(leads.filter((l) => l.leadId !== leadId));
      setMessage({ type: 'success', text: 'Lead deleted' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to delete lead' });
    }
  };

  const handleSimulateInbound = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const newLead = await leadsService.createLead({
        name: simulatorForm.name,
        phone: simulatorForm.phone,
        source: simulatorForm.source,
        courseInterest: simulatorForm.courseInterest,
        notes: `Simulated inbound webhook message: "${simulatorForm.message}"`,
      });

      setLeads([newLead, ...leads]);
      setMessage({ type: 'success', text: `Simulated Inbound WhatsApp Lead received and attributed to ${simulatorForm.source}!` });
      setActiveView('kanban');
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Simulation failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const exportLeadsCsv = () => {
    const headers = ['Lead ID', 'Name', 'Email', 'Phone', 'Source', 'Status', 'Course Interest', 'Created At'];
    const rows = filteredLeads.map(l => [
      l.leadId,
      `"${l.name}"`,
      l.email || '',
      l.phone || '',
      l.source || '',
      l.status || '',
      `"${l.courseInterest || ''}"`,
      l.createdAt || ''
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `cloudbinary_leads_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const stages: { id: LeadStatus; label: string; color: string; bg: string; border: string }[] = [
    { id: 'enquired', label: '1. New / Inbound', color: 'text-blue-400', bg: 'bg-blue-950/40', border: 'border-blue-800/60' },
    { id: 'contacted', label: '2. Contacted', color: 'text-indigo-400', bg: 'bg-indigo-950/40', border: 'border-indigo-800/60' },
    { id: 'counselling', label: '3. Counseling / Demo', color: 'text-purple-400', bg: 'bg-purple-950/40', border: 'border-purple-800/60' },
    { id: 'registered', label: '4. Registered / Token', color: 'text-pink-400', bg: 'bg-pink-950/40', border: 'border-pink-800/60' },
    { id: 'enrolled', label: '5. Enrolled (Admissions)', color: 'text-emerald-400', bg: 'bg-emerald-950/40', border: 'border-emerald-800/60' },
    { id: 'dropped', label: '6. Dropped / Lost', color: 'text-slate-400', bg: 'bg-slate-900/40', border: 'border-slate-800' },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Toast message */}
      {message && (
        <div className={`p-4 rounded-xl text-xs font-semibold flex items-center justify-between shadow-lg ${
          message.type === 'success' ? 'bg-emerald-950/90 text-emerald-300 border border-emerald-800/80' : 'bg-rose-950/90 text-rose-300 border border-rose-800/80'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-rose-400" />}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header & Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800/80 p-5 rounded-2xl shadow-lg">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-white tracking-tight font-heading">
              Inbound Leads & Pipeline
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
              {filteredLeads.length} Candidates
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Track candidates across WhatsApp, Meta Ads, Google Ads campaigns with stage progression.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View switcher */}
          <div className="flex items-center bg-slate-950 border border-slate-800 rounded-xl p-1">
            <button
              onClick={() => setActiveView('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeView === 'kanban' ? 'bg-indigo-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Kanban
            </button>
            <button
              onClick={() => setActiveView('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeView === 'table' ? 'bg-indigo-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <List className="w-3.5 h-3.5" />
              Table
            </button>
            <button
              onClick={() => setActiveView('simulator')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeView === 'simulator' ? 'bg-emerald-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Simulate Inbound
            </button>
          </div>

          <button
            onClick={exportLeadsCsv}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold border border-slate-700 transition-all"
            title="Export CSV"
          >
            <Download className="w-3.5 h-3.5" />
            Export
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-600/30 transition-all"
          >
            <Plus className="w-4 h-4" />
            Add Lead
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-center gap-3 bg-slate-900/60 border border-slate-800/80 p-3 rounded-xl">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by candidate name, phone (+91), email, or course..."
            className="w-full pl-10 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
          >
            <option value="all">All Stages</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
          >
            <option value="all">All Sources</option>
            <option value="meta-ads">Meta / Instagram Ads</option>
            <option value="google-ads">Google Ads</option>
            <option value="youtube">YouTube</option>
            <option value="website">Website</option>
            <option value="referral">Referral / Alumni</option>
          </select>
        </div>
      </div>

      {/* VIEW 1: KANBAN BOARD */}
      {activeView === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-start overflow-x-auto pb-4">
          {stages.map((stage) => {
            const stageLeads = filteredLeads.filter((l) => l.status === stage.id);

            return (
              <div
                key={stage.id}
                className={`rounded-2xl border ${stage.border} ${stage.bg} p-3 flex flex-col min-w-[240px] max-h-[calc(100vh-16rem)]`}
              >
                {/* Stage Header */}
                <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-800/80">
                  <span className={`text-xs font-bold tracking-tight ${stage.color}`}>
                    {stage.label}
                  </span>
                  <span className="text-[11px] font-mono font-bold bg-slate-900/90 text-slate-300 border border-slate-700/60 px-2 py-0.5 rounded-full">
                    {stageLeads.length}
                  </span>
                </div>

                {/* Cards Container */}
                <div className="space-y-2.5 overflow-y-auto pr-1 flex-1">
                  {stageLeads.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 text-[11px] italic">
                      No candidates in this stage
                    </div>
                  ) : (
                    stageLeads.map((lead) => (
                      <div
                        key={lead.leadId}
                        className="bg-slate-900/95 border border-slate-800 hover:border-indigo-500/60 rounded-xl p-3 shadow-md space-y-2.5 transition-all group relative"
                      >
                        {/* Top: Name & Source Tag */}
                        <div className="flex items-start justify-between gap-1">
                          <div>
                            <h4 className="font-bold text-white text-xs tracking-tight group-hover:text-indigo-300 transition-colors">
                              {lead.name}
                            </h4>
                            <p className="text-[11px] text-slate-400 font-mono flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3 text-emerald-400" />
                              {lead.phone}
                            </p>
                          </div>
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-slate-800 text-slate-300 border border-slate-700">
                            {lead.source}
                          </span>
                        </div>

                        {/* Course Interest */}
                        <div className="text-[11px] text-slate-300 bg-slate-950/60 px-2 py-1 rounded-md border border-slate-800/80 truncate">
                          {lead.courseInterest || 'FDE Program'}
                        </div>

                        {/* Fee Status pill if set */}
                        {lead.fee && lead.fee > 0 && (
                          <div className="flex items-center justify-between text-[10px] text-slate-400 bg-slate-950/80 px-2 py-1 rounded border border-slate-800">
                            <span>Agreed Fee:</span>
                            <span className="font-bold text-emerald-400">₹{lead.fee.toLocaleString('en-IN')}</span>
                          </div>
                        )}

                        {/* Actions row */}
                        <div className="flex items-center justify-between pt-1 border-t border-slate-800/80 text-xs">
                          {/* WhatsApp Chat Button */}
                          <a
                            href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-950/80 hover:bg-emerald-900 text-emerald-400 border border-emerald-800/60 rounded-md text-[10px] font-semibold transition-colors"
                            title="Direct WhatsApp Message"
                          >
                            <MessageCircle className="w-3 h-3" />
                            WhatsApp
                          </a>

                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setSelectedLead(lead);
                                setShowFollowUpModal(true);
                              }}
                              className="p-1 text-slate-400 hover:text-indigo-400 hover:bg-slate-800 rounded transition-colors"
                              title="Add Counselor Follow-up Note"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </button>

                            {lead.status !== 'enrolled' && (
                              <button
                                onClick={() => {
                                  setSelectedLead(lead);
                                  setShowConvertModal(true);
                                }}
                                className="p-1 text-emerald-400 hover:bg-emerald-950 rounded transition-colors"
                                title="Convert to Enrolled Student"
                              >
                                <UserCheck className="w-3.5 h-3.5" />
                              </button>
                            )}

                            <button
                              onClick={() => handleDeleteLead(lead.leadId)}
                              className="p-1 text-slate-500 hover:text-rose-400 hover:bg-slate-800 rounded transition-colors"
                              title="Delete Lead"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>

                        {/* Quick stage mover dropdown */}
                        <div className="pt-1">
                          <select
                            value={lead.status}
                            onChange={(e) => handleStatusChange(lead.leadId, e.target.value as LeadStatus)}
                            className="w-full text-[10px] bg-slate-950 border border-slate-800 text-slate-300 rounded px-1.5 py-1 focus:outline-none focus:border-indigo-500"
                          >
                            {stages.map((s) => (
                              <option key={s.id} value={s.id}>Move to: {s.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* VIEW 2: DATA TABLE */}
      {activeView === 'table' && (
        <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-950 text-slate-400 uppercase tracking-wider text-[10px] border-b border-slate-800">
                <tr>
                  <th className="p-4">Candidate</th>
                  <th className="p-4">Contact Info</th>
                  <th className="p-4">Program Interest</th>
                  <th className="p-4">Source</th>
                  <th className="p-4">Stage</th>
                  <th className="p-4">Follow-ups</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 font-medium">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-500 text-xs">
                      No leads matching your search criteria.
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => (
                    <tr key={lead.leadId} className="hover:bg-slate-800/40 transition-colors">
                      <td className="p-4">
                        <div className="font-bold text-white text-sm">{lead.name}</div>
                        <div className="text-[11px] text-slate-400">Created: {new Date(lead.createdAt).toLocaleDateString()}</div>
                      </td>
                      <td className="p-4 space-y-0.5">
                        <div className="font-mono text-emerald-400 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </div>
                        {lead.email && (
                          <div className="text-slate-400 flex items-center gap-1 truncate max-w-[180px]">
                            <Mail className="w-3 h-3" />
                            {lead.email}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-slate-200 font-semibold">{lead.courseInterest || 'FDE Program'}</span>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-800 text-slate-300 border border-slate-700 uppercase">
                          {lead.source}
                        </span>
                      </td>
                      <td className="p-4">
                        <select
                          value={lead.status}
                          onChange={(e) => handleStatusChange(lead.leadId, e.target.value as LeadStatus)}
                          className="text-xs bg-slate-950 border border-slate-800 text-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-indigo-500 font-semibold"
                        >
                          {stages.map((s) => (
                            <option key={s.id} value={s.id}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-400 text-xs font-mono">
                          {lead.followUps?.length || 0} notes
                        </span>
                      </td>
                      <td className="p-4 text-right space-x-2">
                        <a
                          href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-950/80 hover:bg-emerald-900 text-emerald-400 border border-emerald-800/60 rounded-lg text-xs font-semibold transition-colors"
                        >
                          <MessageCircle className="w-3 h-3" />
                          Chat
                        </a>
                        <button
                          onClick={() => {
                            setSelectedLead(lead);
                            setShowFollowUpModal(true);
                          }}
                          className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold transition-colors"
                        >
                          Note
                        </button>
                        <button
                          onClick={() => handleDeleteLead(lead.leadId)}
                          className="p-1.5 text-slate-500 hover:text-rose-400 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VIEW 3: INBOUND WEBHOOK SIMULATOR */}
      {activeView === 'simulator' && (
        <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-xl max-w-2xl mx-auto space-y-6">
          <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white font-heading">
                WhatsApp Inbound Lead Simulator
              </h2>
              <p className="text-xs text-slate-400">
                Simulates real webhook events from Meta Ads, Google Ads, or YouTube to test lead attribution and automatic ingestion.
              </p>
            </div>
          </div>

          <form onSubmit={handleSimulateInbound} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Candidate Name</label>
                <input
                  type="text"
                  required
                  value={simulatorForm.name}
                  onChange={(e) => setSimulatorForm({ ...simulatorForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Phone Number (+91)</label>
                <input
                  type="tel"
                  required
                  value={simulatorForm.phone}
                  onChange={(e) => setSimulatorForm({ ...simulatorForm, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500 font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Marketing Source Attribution</label>
              <select
                value={simulatorForm.source}
                onChange={(e) => setSimulatorForm({ ...simulatorForm, source: e.target.value })}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
              >
                <option value="meta-ads">Meta / Instagram Ads ([REF:META_ADS])</option>
                <option value="google-ads">Google Search / PMax Ads ([REF:GOOGLE_ADS])</option>
                <option value="youtube">YouTube Video Links ([REF:YOUTUBE])</option>
                <option value="website">Website Direct ([REF:WEBSITE])</option>
                <option value="referral">Alumni Referral ([REF:ALUMNI])</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Inbound WhatsApp Message Body</label>
              <textarea
                rows={3}
                required
                value={simulatorForm.message}
                onChange={(e) => setSimulatorForm({ ...simulatorForm, message: e.target.value })}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
              />
            </div>

            <button
              type="submit"
              disabled={actionLoading}
              className="w-full py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white rounded-xl text-xs font-bold shadow-lg shadow-emerald-900/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send Simulated Webhook & Ingest to CRM
            </button>
          </form>
        </div>
      )}

      {/* MODAL 1: CREATE LEAD */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-base font-bold text-white font-heading">Add New Lead</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateLead} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Candidate Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Suresh Varma"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Phone Number (+91) *</label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 9876543210"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 font-mono focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Email Address</label>
                  <input
                    type="email"
                    placeholder="candidate@example.com"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Marketing Source</label>
                  <select
                    value={createForm.source}
                    onChange={(e) => setCreateForm({ ...createForm, source: e.target.value as LeadSource })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="meta-ads">Meta / Instagram Ads</option>
                    <option value="google-ads">Google Ads</option>
                    <option value="youtube">YouTube</option>
                    <option value="website">Website</option>
                    <option value="referral">Referral</option>
                    <option value="direct">Direct Walk-in</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Program of Interest</label>
                  <select
                    value={createForm.courseInterest}
                    onChange={(e) => setCreateForm({ ...createForm, courseInterest: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="Forward Deployed Engineer (FDE)">Forward Deployed Engineer (FDE)</option>
                    <option value="DevOps & AWS Cloud Masterclass">DevOps & AWS Cloud Masterclass</option>
                    <option value="Cloud FinOps Practitioner">Cloud FinOps Practitioner</option>
                    <option value="Linux Incident Response & SRE">Linux Incident Response & SRE</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Initial Counselor Notes</label>
                <textarea
                  rows={3}
                  placeholder="Candidate background, current CTC, career goal..."
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md shadow-indigo-600/30"
                >
                  {actionLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Create Lead
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: ADD FOLLOW-UP NOTE */}
      {showFollowUpModal && selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-base font-bold text-white font-heading">Counselor Activity: {selectedLead.name}</h3>
                <p className="text-xs text-slate-400">{selectedLead.phone} • {selectedLead.courseInterest}</p>
              </div>
              <button onClick={() => setShowFollowUpModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Existing Timeline */}
            <div className="max-h-40 overflow-y-auto space-y-2 pr-1 border-b border-slate-800 pb-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase">Past Follow-up History</span>
              {(!selectedLead.followUps || selectedLead.followUps.length === 0) ? (
                <p className="text-xs text-slate-500 italic">No previous notes recorded.</p>
              ) : (
                selectedLead.followUps.map((f, idx) => (
                  <div key={idx} className="bg-slate-950/70 border border-slate-800 p-2.5 rounded-lg text-xs space-y-1">
                    <div className="flex items-center justify-between text-[10px] text-slate-400">
                      <span className="font-bold text-indigo-400 uppercase">{f.type || 'Note'}</span>
                      <span>{new Date(f.date).toLocaleString()}</span>
                    </div>
                    <p className="text-slate-200">{f.note}</p>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleAddFollowUp} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Interaction Type</label>
                  <select
                    value={followUpForm.type}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, type: e.target.value as any })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="call">Phone Call</option>
                    <option value="whatsapp_outbound">WhatsApp Message</option>
                    <option value="meeting">Counseling Demo / Zoom</option>
                    <option value="note">Internal Counselor Note</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Update Status (Optional)</label>
                  <select
                    value={followUpForm.newStatus}
                    onChange={(e) => setFollowUpForm({ ...followUpForm, newStatus: e.target.value as any })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Keep Current Status ({selectedLead.status})</option>
                    {stages.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Counselor Discussion Note *</label>
                <textarea
                  rows={3}
                  required
                  placeholder="Summary of conversation, questions asked, batch timing preference..."
                  value={followUpForm.note}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, note: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowFollowUpModal(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md shadow-indigo-600/30"
                >
                  {actionLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save Activity Note
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: CONVERT TO ENROLLED STUDENT */}
      {showConvertModal && selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                  <UserCheck className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white font-heading">Confirm Student Admission</h3>
                  <p className="text-xs text-slate-400">{selectedLead.name}</p>
                </div>
              </div>
              <button onClick={() => setShowConvertModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              Converting this lead will mark them as <span className="text-emerald-400 font-bold">Enrolled</span> and add them to the Student Admissions & Fees Directory.
            </p>

            <form onSubmit={handleConvertLead} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Conversion Note</label>
                <textarea
                  rows={2}
                  value={convertForm.notes}
                  onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowConvertModal(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md shadow-emerald-900/30"
                >
                  {actionLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Confirm Admission
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
